import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Package, Plus, Minus } from '@phosphor-icons/react';

export const MaterialsList = ({ materials, selectedTier, isEditing, onMaterialsChange }) => {
  if (!materials || materials.length === 0) return null;
  
  const priceKey = selectedTier === 'economy' ? 'total_low' : selectedTier === 'premium' ? 'total_high' : 'total_mid';
  const unitPriceKey = selectedTier === 'economy' ? 'unit_price_low' : selectedTier === 'premium' ? 'unit_price_high' : 'unit_price_mid';
  
  const handleMaterialChange = (idx, field, value) => {
    if (onMaterialsChange) {
      const updated = [...materials];
      updated[idx] = { ...updated[idx], [field]: value };
      if (field === 'quantity' || field.startsWith('unit_price_')) {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : updated[idx].quantity;
        const unitLow = field === 'unit_price_low' ? parseFloat(value) || 0 : updated[idx].unit_price_low;
        const unitMid = field === 'unit_price_mid' ? parseFloat(value) || 0 : updated[idx].unit_price_mid;
        const unitHigh = field === 'unit_price_high' ? parseFloat(value) || 0 : updated[idx].unit_price_high;
        updated[idx].total_low = qty * unitLow;
        updated[idx].total_mid = qty * unitMid;
        updated[idx].total_high = qty * unitHigh;
      }
      onMaterialsChange(updated);
    }
  };
  
  const handleAddMaterial = () => {
    if (onMaterialsChange) {
      onMaterialsChange([...materials, {
        item: 'New Item',
        quantity: 1,
        unit: 'each',
        unit_price_low: 0,
        unit_price_mid: 0,
        unit_price_high: 0,
        total_low: 0,
        total_mid: 0,
        total_high: 0,
        notes: ''
      }]);
    }
  };
  
  const handleRemoveMaterial = (idx) => {
    if (onMaterialsChange) {
      onMaterialsChange(materials.filter((_, i) => i !== idx));
    }
  };
  
  return (
    <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white shadow-[4px_4px_0px_0px_#000000] dark:shadow-[4px_4px_0px_0px_#2B4C6F]">
      <div className="p-4 border-b-2 border-black dark:border-white bg-neutral-50 dark:bg-neutral-900 flex items-center justify-between">
        <div>
          <h4 className="font-black text-neutral-900 dark:text-white uppercase tracking-wider text-sm flex items-center gap-2">
            <Package size={18} weight="bold" className="text-[#2B4C6F]" />
            MATERIALS LIST
            {isEditing && <Badge className="bg-amber-500 text-white text-[10px] ml-2">EDITING</Badge>}
          </h4>
          <p className="text-xs text-neutral-500 mt-1">Quantities and pricing for {selectedTier} tier</p>
        </div>
        {isEditing && (
          <Button
            onClick={handleAddMaterial}
            size="sm"
            className="h-8 px-3 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold border-2 border-black rounded-none"
          >
            <Plus size={14} className="mr-1" /> Add Item
          </Button>
        )}
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-black dark:border-white bg-neutral-100 dark:bg-neutral-800">
              <th className="text-left p-3 font-bold uppercase tracking-wider text-xs">Item</th>
              <th className="text-right p-3 font-bold uppercase tracking-wider text-xs">Qty</th>
              <th className="text-right p-3 font-bold uppercase tracking-wider text-xs">Unit</th>
              <th className="text-right p-3 font-bold uppercase tracking-wider text-xs">Unit Price</th>
              <th className="text-right p-3 font-bold uppercase tracking-wider text-xs">Total</th>
              {isEditing && <th className="p-3 w-10"></th>}
            </tr>
          </thead>
          <tbody>
            {materials.map((mat, idx) => (
              <tr key={idx} className="border-b border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800">
                <td className="p-3">
                  {isEditing ? (
                    <input
                      type="text"
                      value={mat.item}
                      onChange={(e) => handleMaterialChange(idx, 'item', e.target.value)}
                      className="w-full font-medium text-neutral-900 dark:text-white bg-transparent border-b border-dashed border-neutral-300 focus:border-[#2B4C6F] outline-none"
                    />
                  ) : (
                    <>
                      <span className="font-medium text-neutral-900 dark:text-white">{mat.item}</span>
                      {mat.notes && <p className="text-xs text-neutral-500 mt-1">{mat.notes}</p>}
                    </>
                  )}
                </td>
                <td className="p-3 text-right">
                  {isEditing ? (
                    <input
                      type="number"
                      value={mat.quantity}
                      onChange={(e) => handleMaterialChange(idx, 'quantity', e.target.value)}
                      className="w-16 text-right font-mono text-neutral-700 dark:text-neutral-300 bg-transparent border-b border-dashed border-neutral-300 focus:border-[#2B4C6F] outline-none"
                    />
                  ) : (
                    <span className="font-mono text-neutral-700 dark:text-neutral-300">{mat.quantity}</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {isEditing ? (
                    <input
                      type="text"
                      value={mat.unit}
                      onChange={(e) => handleMaterialChange(idx, 'unit', e.target.value)}
                      className="w-16 text-right text-neutral-500 bg-transparent border-b border-dashed border-neutral-300 focus:border-[#2B4C6F] outline-none"
                    />
                  ) : (
                    <span className="text-neutral-500">{mat.unit}</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {isEditing ? (
                    <div className="flex items-center justify-end">
                      <span className="text-neutral-500 mr-1">$</span>
                      <input
                        type="number"
                        value={mat[unitPriceKey] || 0}
                        onChange={(e) => handleMaterialChange(idx, unitPriceKey, e.target.value)}
                        className="w-20 text-right font-mono text-neutral-700 dark:text-neutral-300 bg-transparent border-b border-dashed border-neutral-300 focus:border-[#2B4C6F] outline-none"
                      />
                    </div>
                  ) : (
                    <span className="font-mono text-neutral-700 dark:text-neutral-300">${mat[unitPriceKey]?.toLocaleString() || 0}</span>
                  )}
                </td>
                <td className="p-3 text-right font-mono font-bold text-neutral-900 dark:text-white">
                  ${mat[priceKey]?.toLocaleString() || 0}
                </td>
                {isEditing && (
                  <td className="p-3">
                    <button
                      onClick={() => handleRemoveMaterial(idx)}
                      className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    >
                      <Minus size={16} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-[#2B4C6F]/10 dark:bg-[#2B4C6F]/20">
              <td colSpan={isEditing ? "5" : "4"} className="p-3 font-bold uppercase tracking-wider text-sm text-right">Materials Subtotal</td>
              <td className="p-3 text-right font-mono font-black text-lg text-[#2B4C6F]">
                ${materials.reduce((sum, mat) => sum + (mat[priceKey] || 0), 0).toLocaleString()}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
